// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { MembershipReportData } from '@/membership/api/membership-types';
import { MembersSerialsDialogToggle } from '@/membership/components/members-serials-toggle';
import { MembersView } from '@/membership/components/members-view';
import { MembershipViewState } from '@/membership/hooks/use-membership-view';
import {
  Alert,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

// The dialog is shorter than the tab, so the charts are too.
const DIALOG_CHART_HEIGHT = 220;
const DIALOG_DELTA_HEIGHT = 80;

interface PreviewContentProps {
  report: MembershipReportData | null;
  view: MembershipViewState;
  isSerialsOpen: boolean;
  onToggleSerials: () => void;
  isLoading: boolean;
  errorMessage: string | null;
  isScopeComplete: boolean;
}

interface Props extends PreviewContentProps {
  title: string;
  caption: string;
}

/**
 * One membership report inside the dialog, under the rules that produced it.
 * The pair is what shows the effect of the leave rule, so both keep the same
 * shape whatever state they are in.
 */
export function MembershipReportPreview(props: Props) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2">{props.title}</Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1.5 }}
      >
        {props.caption}
      </Typography>
      <PreviewContent
        report={props.report}
        view={props.view}
        isSerialsOpen={props.isSerialsOpen}
        onToggleSerials={props.onToggleSerials}
        isLoading={props.isLoading}
        errorMessage={props.errorMessage}
        isScopeComplete={props.isScopeComplete}
      />
    </Paper>
  );
}

function PreviewContent(props: PreviewContentProps) {
  if (!props.isScopeComplete) {
    return (
      <Typography variant="body2" color="text.secondary">
        Pick at least one SKU and one join rule to see a preview
      </Typography>
    );
  }
  // Shown whether or not a report is already on screen. A rerun that fails
  // leaves the last one there, and undimming it would present an answer to
  // rules the user has since changed as though it were current. An edit clears
  // this as it lands, so the error it is fixing does not outlive it.
  if (props.errorMessage !== null) {
    return <Alert severity="error">{props.errorMessage}</Alert>;
  }
  if (props.report === null) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Building the chart
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack sx={{ opacity: props.isLoading ? 0.4 : 1 }}>
      <MembersView
        report={props.report}
        view={props.view}
        chartHeight={DIALOG_CHART_HEIGHT}
        deltaChartHeight={DIALOG_DELTA_HEIGHT}
        serialsControl={
          <MembersSerialsDialogToggle
            dayDate={props.view.dayDate}
            isOpen={props.isSerialsOpen}
            onToggle={props.onToggleSerials}
          />
        }
      />
    </Stack>
  );
}
